import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import {
  Checkbox,
  Dropdown,
  Icon,
  Message,
  Modal,
  Table
} from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';

import Button from '../../components/Button';
import { iRootState } from '../../store';
import { CustomDropDownOptions } from '../../types';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import { FieldLabel } from '../../styles';
import {
  importWalletProperties,
  ImportWalletPropertyPayload,
  ImportWalletPropertiesResult
} from '../../services/properties';
import { walletPropertyTypeOptions } from '../properties-wallet/constants';
import { successNotification, warningNotification } from '../../services/notification';
import { isBackofficeTestUser } from '../../util';
import {
  ActionsBar,
  ActionsGroup,
  ContentSegment,
  DataCell,
  Dropzone,
  ExpectedFieldsTable,
  HeaderPreview,
  ImportShell,
  MatchColumn,
  MatchGrid,
  MatchPreview,
  MatchSelect,
  MutedText,
  PageContainer,
  PageHeader,
  PageSubtitle,
  PageTitle,
  ScrollArea,
  StatusMessage,
  StepCircle,
  StepConnector,
  StepLabel,
  StepsBar,
  StyledTable,
  TableWrapper,
  ValidationTools
} from './styles';

type Step = 1 | 2 | 3 | 4;

type ImportField = {
  key: string;
  label: string;
  required?: boolean;
  aliases: string[];
  example: string;
};

type ImportedPropertyMetadata = {
  occupation: string;
  purpose: string;
  commercialActivity: string;
  rentalGuaranteeType: string;
  rentalGuaranteeInsurer: string;
  hasFireInsurance: string;
  fireInsuranceInsurer: string;
  fireInsuranceStartDate: string;
  fireInsuranceEndDate: string;
  policyNumber: string;
  itemCode: string;
  policyStatus: string;
};

export type ImportedRow = {
  rowNumber: number;
  values: Record<string, string>;
  parsed: {
    identification: string;
    propertyType: string;
    area?: number;
    zipcode: string;
    state: string;
    city: string;
    street: string;
    neighborhood: string;
    number: string;
    complement?: string;
    metadata: ImportedPropertyMetadata;
  };
  errors: Partial<Record<string, string>>;
};

type NormalizedField = {
  value: string;
  error?: string;
};

type MetadataNormalizationResult = {
  values: Record<string, string>;
  metadata: ImportedPropertyMetadata;
  errors: Partial<Record<string, string>>;
};

const fields: ImportField[] = [
  {
    key: 'identification',
    label: 'Identificação',
    required: true,
    aliases: ['identification', 'identificação', 'codigo', 'código', 'imóvel', 'imovel'],
    example: 'Ex.: Imóvel 100'
  },
  {
    key: 'propertyType',
    label: 'Tipo',
    required: true,
    aliases: ['type_id', 'tipo', 'tipo do imóvel', 'tipo do imovel', 'propertyType'],
    example: 'Ex.: Casa'
  },
  {
    key: 'area',
    label: 'Área',
    aliases: ['area', 'área'],
    example: 'Ex.: 100'
  },
  {
    key: 'zipcode',
    label: 'CEP',
    required: true,
    aliases: ['zipcode', 'cep', 'código postal', 'codigo postal', 'address.zipcode'],
    example: 'Ex.: 01000-000'
  },
  {
    key: 'street',
    label: 'Rua',
    required: true,
    aliases: ['street', 'rua', 'logradouro', 'address.street'],
    example: 'Ex.: Rua Dona Mariquita'
  },
  {
    key: 'number',
    label: 'Número',
    required: true,
    aliases: ['number', 'número', 'numero', 'address.number'],
    example: 'Ex.: 100'
  },
  {
    key: 'neighborhood',
    label: 'Bairro',
    required: true,
    aliases: ['neighborhood', 'bairro', 'address.neighborhood'],
    example: 'Ex.: Fazenda Morumbi'
  },
  {
    key: 'city',
    label: 'Cidade',
    required: true,
    aliases: ['city', 'cidade', 'address.city'],
    example: 'Ex.: São Paulo'
  },
  {
    key: 'state',
    label: 'UF',
    required: true,
    aliases: ['state', 'uf', 'estado', 'address.state'],
    example: 'Ex.: SP'
  },
  {
    key: 'complement',
    label: 'Complemento',
    aliases: ['complement', 'complemento', 'address.complement'],
    example: 'Ex.: Bloco 25'
  },
  {
    key: 'occupation',
    label: 'Ocupação do imóvel',
    aliases: ['occupation', 'ocupação', 'ocupacao'],
    example: 'Ex.: Ocupado'
  },
  {
    key: 'purpose',
    label: 'Finalidade',
    aliases: ['purpose', 'finalidade'],
    example: 'Ex.: Residencial'
  },
  {
    key: 'commercialActivity',
    label: 'Atividade comercial',
    aliases: [
      'commercialActivity',
      'commercial_activity',
      'atividade comercial',
      'tipo de atividade comercial',
      'atividade'
    ],
    example: 'Ex.: Comércio'
  },
  {
    key: 'rentalGuaranteeType',
    label: 'Tipo de garantia locatícia',
    aliases: [
      'rentalGuaranteeType',
      'rental_guarantee_type',
      'tipo de garantia',
      'garantia locatícia',
      'garantia locaticia'
    ],
    example: 'Ex.: Seguro fiança'
  },
  {
    key: 'rentalGuaranteeInsurer',
    label: 'Seguradora da garantia',
    aliases: [
      'rentalGuaranteeInsurer',
      'rental_guarantee_insurer',
      'seguradora da garantia',
      'seguradora responsável pela garantia',
      'seguradora responsavel pela garantia'
    ],
    example: 'Ex.: Seguradora X'
  },
  {
    key: 'hasFireInsurance',
    label: 'Possui seguro incêndio',
    aliases: [
      'hasFireInsurance',
      'has_fire_insurance',
      'possui seguro incêndio',
      'possui seguro incendio',
      'seguro incêndio',
      'seguro incendio'
    ],
    example: 'Ex.: Sim'
  },
  {
    key: 'fireInsuranceInsurer',
    label: 'Seguradora do seguro incêndio',
    aliases: [
      'fireInsuranceInsurer',
      'fire_insurance_insurer',
      'seguradora do seguro incêndio',
      'seguradora do seguro incendio',
      'seguradora seguro incêndio',
      'seguradora seguro incendio'
    ],
    example: 'Ex.: Seguradora Y'
  },
  {
    key: 'fireInsuranceStartDate',
    label: 'Início da vigência do seguro',
    aliases: [
      'fireInsuranceStartDate',
      'fire_insurance_start_date',
      'início da vigência',
      'inicio da vigencia',
      'início da vigência do seguro',
      'inicio da vigencia do seguro'
    ],
    example: 'Ex.: 01/01/2026'
  },
  {
    key: 'fireInsuranceEndDate',
    label: 'Fim da vigência do seguro',
    aliases: [
      'fireInsuranceEndDate',
      'fire_insurance_end_date',
      'fim da vigência',
      'fim da vigencia',
      'fim da vigência do seguro',
      'fim da vigencia do seguro'
    ],
    example: 'Ex.: 31/12/2026'
  },
  {
    key: 'policyNumber',
    label: 'Número da apólice',
    aliases: [
      'policyNumber',
      'policy_number',
      'número da apólice',
      'numero da apolice',
      'apólice',
      'apolice'
    ],
    example: 'Ex.: 000123'
  },
  {
    key: 'itemCode',
    label: 'Código do item',
    aliases: ['itemCode', 'item_code', 'código do item', 'codigo do item'],
    example: 'Ex.: 001'
  },
  {
    key: 'policyStatus',
    label: 'Situação da apólice',
    aliases: [
      'policyStatus',
      'policy_status',
      'situação da apólice',
      'situacao da apolice',
      'status da apólice',
      'status da apolice'
    ],
    example: 'Ex.: created'
  }
];

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

type Props = ReturnType<typeof mapState>;

const normalizeHeader = (value: string): string =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const formatCell = (value: unknown): string =>
  value === undefined || value === null ? '' : String(value).trim();

const parseNumber = (value: string): number | undefined => {
  if (!value) return undefined;
  const normalized = value.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const normalizeChoice = (
  value: string,
  options: Record<string, string>,
  label: string
): NormalizedField => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return { value: '' };

  const normalizedValue = normalizeHeader(trimmedValue);
  const normalizedOption = options[normalizedValue];

  if (normalizedOption) return { value: normalizedOption };

  return {
    value: trimmedValue,
    error: `${label} inválida. Verifique o valor informado.`
  };
};

const parseDateValue = (value: string): Date | undefined => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return undefined;

  const createDate = (year: number, month: number, day: number): Date | undefined => {
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      return undefined;
    }
    return date;
  };

  let match = /^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{4})$/.exec(trimmedValue);
  if (match) {
    return createDate(Number(match[3]), Number(match[2]), Number(match[1]));
  }

  match = /^(\d{4})[/.\-](\d{1,2})[/.\-](\d{1,2})$/.exec(trimmedValue);
  if (match) {
    return createDate(Number(match[1]), Number(match[2]), Number(match[3]));
  }

  // Excel dates may arrive as their serial number when the worksheet has no
  // explicit date format. The 1899-12-30 base matches SheetJS' date system.
  if (/^\d+(\.\d+)?$/.test(trimmedValue)) {
    const serial = Number(trimmedValue);
    if (serial > 0 && serial < 3000000) {
      const date = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
      if (!Number.isNaN(date.getTime())) return date;
    }
  }

  return undefined;
};

const formatDate = (date: Date): string => {
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getUTCFullYear()}`;
};

const normalizeDate = (value: string, label: string): NormalizedField => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return { value: '' };

  const date = parseDateValue(trimmedValue);
  return date
    ? { value: formatDate(date) }
    : { value: trimmedValue, error: `${label} inválido.` };
};

export const normalizeMetadata = (
  values: Record<string, string>
): MetadataNormalizationResult => {
  const normalizedValues = { ...values };
  const errors: Partial<Record<string, string>> = {};

  const occupation = normalizeChoice(
    values.occupation,
    {
      ocupado: 'Ocupado',
      occupied: 'Ocupado',
      desocupado: 'Desocupado',
      unoccupied: 'Desocupado',
      vacant: 'Desocupado'
    },
    'Ocupação do imóvel'
  );
  normalizedValues.occupation = occupation.value;
  if (occupation.error) errors.occupation = occupation.error;

  const purpose = normalizeChoice(
    values.purpose,
    {
      residencial: 'Residencial',
      residential: 'Residencial',
      comercial: 'Comercial',
      commercial: 'Comercial'
    },
    'Finalidade'
  );
  normalizedValues.purpose = purpose.value;
  if (purpose.error) errors.purpose = purpose.error;

  const hasFireInsurance = normalizeChoice(
    values.hasFireInsurance,
    {
      sim: 'Sim',
      s: 'Sim',
      yes: 'Sim',
      true: 'Sim',
      '1': 'Sim',
      nao: 'Não',
      n: 'Não',
      no: 'Não',
      false: 'Não',
      '0': 'Não'
    },
    'Possui seguro incêndio'
  );
  normalizedValues.hasFireInsurance = hasFireInsurance.value;
  if (hasFireInsurance.error) errors.hasFireInsurance = hasFireInsurance.error;

  const policyStatus = normalizeChoice(
    values.policyStatus,
    {
      created: 'created',
      emitida: 'created',
      emitido: 'created',
      canceled: 'canceled',
      cancelled: 'canceled',
      cancelada: 'canceled',
      cancelado: 'canceled',
      expired: 'expired',
      expirada: 'expired',
      expirado: 'expired'
    },
    'Situação da apólice'
  );
  normalizedValues.policyStatus = policyStatus.value;
  if (policyStatus.error) errors.policyStatus = policyStatus.error;

  const startDate = normalizeDate(
    values.fireInsuranceStartDate,
    'Início da vigência do seguro'
  );
  normalizedValues.fireInsuranceStartDate = startDate.value;
  if (startDate.error) errors.fireInsuranceStartDate = startDate.error;

  const endDate = normalizeDate(
    values.fireInsuranceEndDate,
    'Fim da vigência do seguro'
  );
  normalizedValues.fireInsuranceEndDate = endDate.value;
  if (endDate.error) errors.fireInsuranceEndDate = endDate.error;

  const parsedStartDate = parseDateValue(normalizedValues.fireInsuranceStartDate);
  const parsedEndDate = parseDateValue(normalizedValues.fireInsuranceEndDate);
  if (parsedStartDate && parsedEndDate && parsedStartDate > parsedEndDate) {
    errors.fireInsuranceEndDate =
      'Fim da vigência deve ser igual ou posterior ao início.';
  }

  return {
    values: normalizedValues,
    metadata: {
      occupation: normalizedValues.occupation,
      purpose: normalizedValues.purpose,
      commercialActivity: normalizedValues.commercialActivity,
      rentalGuaranteeType: normalizedValues.rentalGuaranteeType,
      rentalGuaranteeInsurer: normalizedValues.rentalGuaranteeInsurer,
      hasFireInsurance: normalizedValues.hasFireInsurance,
      fireInsuranceInsurer: normalizedValues.fireInsuranceInsurer,
      fireInsuranceStartDate: normalizedValues.fireInsuranceStartDate,
      fireInsuranceEndDate: normalizedValues.fireInsuranceEndDate,
      policyNumber: normalizedValues.policyNumber,
      itemCode: normalizedValues.itemCode,
      policyStatus: normalizedValues.policyStatus
    },
    errors
  };
};

type SortDirection = 'ASC' | 'DESC';

const dateSortFields = new Set(['fireInsuranceStartDate', 'fireInsuranceEndDate']);

const compareImportedRows = (
  left: ImportedRow,
  right: ImportedRow,
  fieldKey: string,
  direction: SortDirection
): number => {
  const leftValue = fieldKey === 'rowNumber' ? String(left.rowNumber) : left.values[fieldKey] || '';
  const rightValue = fieldKey === 'rowNumber' ? String(right.rowNumber) : right.values[fieldKey] || '';
  const leftBlank = !leftValue.trim();
  const rightBlank = !rightValue.trim();

  // Empty cells are always shown last, whichever direction is selected.
  if (leftBlank !== rightBlank) return leftBlank ? 1 : -1;

  let result = 0;
  if (fieldKey === 'rowNumber') {
    result = left.rowNumber - right.rowNumber;
  } else if (dateSortFields.has(fieldKey)) {
    const leftDate = parseDateValue(leftValue);
    const rightDate = parseDateValue(rightValue);
    if (leftDate && rightDate) {
      result = leftDate.getTime() - rightDate.getTime();
    } else if (leftDate !== rightDate) {
      result = leftDate ? -1 : 1;
    } else {
      result = leftValue.localeCompare(rightValue, 'pt-BR', {
        sensitivity: 'base',
        numeric: true
      });
    }
  } else if (fieldKey === 'hasFireInsurance') {
    const insuranceOrder: Record<string, number> = { nao: 0, sim: 1 };
    const leftOrder = insuranceOrder[normalizeHeader(leftValue)];
    const rightOrder = insuranceOrder[normalizeHeader(rightValue)];
    if (leftOrder !== undefined && rightOrder !== undefined) {
      result = leftOrder - rightOrder;
    } else {
      result = leftValue.localeCompare(rightValue, 'pt-BR', {
        sensitivity: 'base',
        numeric: true
      });
    }
  } else {
    result = leftValue.localeCompare(rightValue, 'pt-BR', {
      sensitivity: 'base',
      numeric: true
    });
  }

  if (result !== 0) return direction === 'ASC' ? result : -result;
  return left.rowNumber - right.rowNumber;
};

export const sortImportedRows = (
  rows: ImportedRow[],
  fieldKey: string,
  direction: SortDirection
): ImportedRow[] => {
  return [...rows].sort((left, right) =>
    compareImportedRows(left, right, fieldKey, direction)
  );
};

const getPropertyIdentity = (row: ImportedRow): string => {
  const identity = [
    row.values.identification,
    row.values.zipcode,
    row.values.state,
    row.values.city,
    row.values.street,
    row.values.number,
    row.values.complement
  ]
    .map(normalizeHeader)
    .join('|');

  return identity || `row:${row.rowNumber}`;
};

const getPolicyReferenceDate = (row: ImportedRow): Date | undefined => {
  return (
    parseDateValue(row.values.fireInsuranceStartDate) ||
    parseDateValue(row.values.fireInsuranceEndDate)
  );
};

const selectLatestPolicyRow = (
  current: ImportedRow,
  candidate: ImportedRow
): ImportedRow => {
  const currentDate = getPolicyReferenceDate(current);
  const candidateDate = getPolicyReferenceDate(candidate);

  if (currentDate && candidateDate && currentDate.getTime() !== candidateDate.getTime()) {
    return candidateDate > currentDate ? candidate : current;
  }

  if (currentDate !== candidateDate) {
    return candidateDate ? candidate : current;
  }

  // If there is no usable date, the last occurrence in the spreadsheet is
  // treated as the most recent record.
  return candidate.rowNumber >= current.rowNumber ? candidate : current;
};

export const consolidateImportedRows = (rows: ImportedRow[]): ImportedRow[] => {
  const groupedRows: Record<string, ImportedRow[]> = {};

  rows.forEach(row => {
    const propertyIdentity = getPropertyIdentity(row);
    groupedRows[propertyIdentity] = groupedRows[propertyIdentity] || [];
    groupedRows[propertyIdentity].push(row);
  });

  return Object.keys(groupedRows)
    .map(propertyIdentity => {
      const propertyRows = groupedRows[propertyIdentity];
      return propertyRows.reduce(selectLatestPolicyRow);
    })
    .sort((left, right) => left.rowNumber - right.rowNumber);
};

const resolvePropertyType = (value: string): string => {
  const normalizedValue = normalizeHeader(value);
  const option = walletPropertyTypeOptions.find(current => {
    return (
      normalizeHeader(String(current.value)) === normalizedValue ||
      normalizeHeader(String(current.text)) === normalizedValue
    );
  });

  return option ? String(option.value) : value;
};

const buildInitialMapping = (headers: string[]): Record<string, number | null> => {
  return fields.reduce<Record<string, number | null>>((acc, field) => {
    const headerIndex = headers.findIndex(header =>
      field.aliases.map(normalizeHeader).includes(normalizeHeader(header))
    );
    acc[field.key] = headerIndex >= 0 ? headerIndex : null;
    return acc;
  }, {});
};

const getMissingRequiredFields = (mapping: Record<string, number | null>) => {
  return fields.filter(field => field.required && mapping[field.key] === null);
};

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null) {
    const maybeMessage = (error as { message?: string | string[] }).message;
    if (Array.isArray(maybeMessage)) return maybeMessage.join(', ');
    if (typeof maybeMessage === 'string' && maybeMessage.trim()) return maybeMessage;
  }

  if (error instanceof Error && error.message) return error.message;

  return 'Não foi possível ler a planilha.';
};

const PropertiesImportPage = ({ availableBrokersAndEstates, user }: Props) => {
  const history = useHistory();
  const role = user?.role as UserRolesEnum | undefined;
  const isEstateUser = (role || '').includes('ESTATE');
  const isBackofficeTest = isBackofficeTestUser(user);
  const defaultEstateId =
    availableBrokersAndEstates?.activeEstateId || user?.estate?.id || '';
  const [estateId, setEstateId] = useState(defaultEstateId);
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [mapping, setMapping] = useState<Record<string, number | null>>({});
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [showRequiredModal, setShowRequiredModal] = useState(false);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [discardedRows, setDiscardedRows] = useState<number[]>([]);
  const [sortField, setSortField] = useState('rowNumber');
  const [sortDirection, setSortDirection] = useState<SortDirection>('ASC');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importResult, setImportResult] =
    useState<ImportWalletPropertiesResult | null>(null);

  const estateOptions = useMemo(() => {
    return (availableBrokersAndEstates?.estates || []).map((item: CustomDropDownOptions) => {
      const valueId = typeof item.value === 'string' ? item.value : item.value?.id;
      return {
        key: valueId || String(item.key),
        text: item.text || '',
        value: valueId || ''
      };
    });
  }, [availableBrokersAndEstates?.estates]);

  const headers = useMemo(() => rawRows[headerRowIndex] || [], [
    rawRows,
    headerRowIndex
  ]);

  const dataRows = useMemo(() => rawRows.slice(headerRowIndex + 1), [
    rawRows,
    headerRowIndex
  ]);

  const importedRows = useMemo<ImportedRow[]>(() => {
    const parsedRows = dataRows
      .filter(row => row.some(cell => formatCell(cell)))
      .map((row, index) => {
        const rawValues = fields.reduce<Record<string, string>>((acc, field) => {
          const columnIndex = mapping[field.key];
          acc[field.key] = columnIndex === null || columnIndex === undefined
            ? ''
            : formatCell(row[columnIndex]);
          return acc;
        }, {});
        const normalizedMetadata = normalizeMetadata(rawValues);
        const values = normalizedMetadata.values;

        const area = parseNumber(values.area);
        const parsed = {
          identification: values.identification,
          propertyType: resolvePropertyType(values.propertyType),
          area,
          zipcode: values.zipcode,
          state: values.state,
          city: values.city,
          street: values.street,
          neighborhood: values.neighborhood,
          number: values.number,
          complement: values.complement,
          // These fields are intentionally transient. The import payload below
          // explicitly selects only the fields currently accepted by /properties.
          metadata: normalizedMetadata.metadata
        };
        const errors: Partial<Record<string, string>> = {
          ...normalizedMetadata.errors
        };

        fields.forEach(field => {
          if (field.required && !values[field.key]) {
            errors[field.key] = `${field.label} obrigatório`;
          }
        });

        if (values.area && area === undefined) {
          errors.area = 'Área inválida';
        }

        if (values.propertyType && !resolvePropertyType(values.propertyType)) {
          errors.propertyType = 'Tipo inválido';
        }

        return {
          rowNumber: headerRowIndex + index + 2,
          values,
          parsed,
          errors
        };
      });

    return consolidateImportedRows(parsedRows);
  }, [dataRows, headerRowIndex, mapping]);

  const missingRequiredFields = getMissingRequiredFields(mapping);
  const validRows = importedRows.filter(
    row => !discardedRows.includes(row.rowNumber) && Object.keys(row.errors).length === 0
  );
  const invalidRows = importedRows.filter(row => Object.keys(row.errors).length > 0);
  const visibleRows = importedRows.filter(row => {
    if (discardedRows.includes(row.rowNumber)) return false;
    if (!showOnlyErrors) return true;
    return Object.keys(row.errors).length > 0;
  });
  const sortedVisibleRows = useMemo(
    () => sortImportedRows(visibleRows, sortField, sortDirection),
    [visibleRows, sortField, sortDirection]
  );

  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!isEstateUser && !estateId) {
      warningNotification('Atenção', 'Selecione a imobiliária antes de carregar a planilha.');
      return;
    }

    try {
      setParseError('');
      setImportResult(null);
      setFileName(file.name);
      setSelectedRows([]);
      setDiscardedRows([]);
      setSortField('rowNumber');
      setSortDirection('ASC');

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const parsedRows = XLSX.utils.sheet_to_json<string[]>(worksheet, {
        header: 1,
        defval: ''
      });
      const nonEmptyRows = parsedRows.filter(row => row.some(cell => formatCell(cell)));

      if (!nonEmptyRows.length) {
        throw new Error('Nenhum dado foi encontrado na planilha.');
      }

      setRawRows(nonEmptyRows);
      setHeaderRowIndex(0);
      setMapping(buildInitialMapping(nonEmptyRows[0] || []));
      setCurrentStep(2);
    } catch (error) {
      setRawRows([]);
      setMapping({});
      setParseError(getErrorMessage(error));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: '.xlsx,.xls,.csv'
  });

  const handleHeaderNext = () => {
    setMapping(buildInitialMapping(headers));
    setCurrentStep(3);
  };

  const handleMatchNext = () => {
    if (missingRequiredFields.length) {
      setShowRequiredModal(true);
      return;
    }
    setSelectedRows([]);
    setDiscardedRows([]);
    setCurrentStep(4);
  };

  const handleDiscardSelected = () => {
    setDiscardedRows(prev => Array.from(new Set([...prev, ...selectedRows])));
    setSelectedRows([]);
  };

  const handleImport = async () => {
    if (isBackofficeTest) {
      return;
    }

    if (!isEstateUser && !estateId) {
      warningNotification('Atenção', 'Selecione a imobiliária para importar.');
      return;
    }

    if (!validRows.length) {
      warningNotification('Atenção', 'Não há linhas válidas para importar.');
      return;
    }

    const payload: ImportWalletPropertyPayload[] = validRows.map(row => ({
      estateId: isEstateUser ? undefined : estateId,
      identification: row.parsed.identification,
      propertyType: row.parsed.propertyType,
      area: row.parsed.area,
      address: {
        zipcode: row.parsed.zipcode,
        state: row.parsed.state.toUpperCase(),
        city: row.parsed.city,
        street: row.parsed.street,
        neighborhood: row.parsed.neighborhood,
        number: row.parsed.number,
        complement: row.parsed.complement || undefined
      }
    }));

    try {
      setIsSubmitting(true);
      const result = await importWalletProperties(payload);
      setImportResult(result);

      if (result.failed.length) {
        warningNotification(
          'Importação concluída com pendências',
          `${result.success} imóveis importados e ${result.failed.length} com erro.`
        );
      } else {
        successNotification('Sucesso', `${result.success} imóveis importados com sucesso.`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSelectedRow = (rowNumber: number) => {
    setSelectedRows(prev =>
      prev.includes(rowNumber)
        ? prev.filter(current => current !== rowNumber)
        : [...prev, rowNumber]
    );
  };

  const renderSteps = () => {
    const steps = [
      'Carregar ficheiro',
      'Selecionar linha de cabeçalho',
      'Corresponder colunas',
      'Validar os dados'
    ];

    return (
      <StepsBar>
        {steps.map((label, index) => {
          const stepNumber = index + 1;
          const completed = currentStep > stepNumber;
          const active = currentStep === stepNumber;

          return (
            <React.Fragment key={label}>
              <StepLabel active={active || completed}>
                <StepCircle active={active} completed={completed}>
                  {completed ? <Icon name="check" /> : stepNumber}
                </StepCircle>
                {label}
              </StepLabel>
              {stepNumber < steps.length && <StepConnector completed={completed} />}
            </React.Fragment>
          );
        })}
      </StepsBar>
    );
  };

  const renderUploadStep = () => (
    <>
      <PageHeader>
        <PageTitle>Carregar ficheiro</PageTitle>
      </PageHeader>

      {!isEstateUser && (
        <div style={{ maxWidth: 420, marginBottom: 16 }}>
          <FieldLabel>Imobiliária</FieldLabel>
          <Dropdown
            fluid
            value={estateId}
            placeholder="Selecionar..."
            options={estateOptions}
            search
            selection
            onChange={(_, { value }) => setEstateId((value as string) || '')}
          />
        </div>
      )}

      <h3>Dados esperados:</h3>
      <MutedText>
        Terá a oportunidade de renomear ou remover colunas nos próximos passos.
      </MutedText>
      <ExpectedFieldsTable>
        {fields.map(field => (
          <div key={field.key}>
            <strong>{field.label}</strong>
            <span>{field.example}</span>
          </div>
        ))}
      </ExpectedFieldsTable>

      <Dropzone {...getRootProps()} isDragActive={isDragActive}>
        <input {...getInputProps()} />
        <div>
          <h3>Carregar ficheiro .xlsx, .xls ou .csv</h3>
          <Button type="button" text="Selecione o ficheiro" />
          {fileName && <p>{fileName}</p>}
        </div>
      </Dropzone>

      {parseError && (
        <StatusMessage negative content={parseError} style={{ marginTop: 16 }} />
      )}
    </>
  );

  const renderHeaderStep = () => (
    <>
      <PageHeader>
        <PageTitle>Selecionar linha de cabeçalho</PageTitle>
      </PageHeader>
      <TableWrapper>
        <StyledTable celled compact>
          <Table.Body>
            {rawRows.slice(0, 30).map((row, index) => (
              <Table.Row
                key={index}
                active={index === headerRowIndex}
                onClick={() => setHeaderRowIndex(index)}
              >
                <Table.Cell collapsing>
                  <Checkbox
                    radio
                    checked={index === headerRowIndex}
                    onChange={() => setHeaderRowIndex(index)}
                  />
                </Table.Cell>
                {row.slice(0, 20).map((cell, cellIndex) => (
                  <Table.Cell key={`${index}-${cellIndex}`}>
                    {formatCell(cell) || '-'}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </StyledTable>
      </TableWrapper>
    </>
  );

  const renderMatchStep = () => {
    const dropdownOptions = fields.map(field => ({
      key: field.key,
      value: field.key,
      text: field.required ? `${field.label} *` : field.label
    }));

    return (
      <>
        <PageHeader>
          <PageTitle>Corresponder colunas</PageTitle>
        </PageHeader>

        <h3>A sua tabela</h3>
        <ScrollArea>
          <MatchGrid columnCount={headers.length}>
            {headers.map((header, index) => (
              <MatchColumn key={`${header}-${index}`}>
                <HeaderPreview>
                  <strong>{header || `Coluna ${index + 1}`}</strong>
                  <button
                    type="button"
                    onClick={() =>
                      setMapping(prev => {
                        const next = { ...prev };
                        Object.keys(next).forEach(key => {
                          if (next[key] === index) next[key] = null;
                        });
                        return next;
                      })
                    }
                  >
                    x
                  </button>
                </HeaderPreview>
                {dataRows.slice(0, 2).map((row, rowIndex) => (
                  <MatchPreview key={rowIndex}>{formatCell(row[index]) || '-'}</MatchPreview>
                ))}
              </MatchColumn>
            ))}
          </MatchGrid>
        </ScrollArea>

        <h3>Tornar-se-á</h3>
        <ScrollArea allowDropdownOverflow>
          <MatchGrid columnCount={headers.length}>
            {headers.map((header, index) => {
              const mappedField = Object.keys(mapping).find(key => mapping[key] === index);

              return (
                <MatchColumn key={`${header}-${index}-map`}>
                  <MatchSelect
                    value={mappedField || ''}
                    onChange={event => {
                      const fieldKey = event.target.value;
                      setMapping(prev => {
                        const next = { ...prev };
                        Object.keys(next).forEach(key => {
                          if (next[key] === index) next[key] = null;
                        });
                        if (fieldKey) next[fieldKey] = index;
                        return next;
                      });
                    }}
                  >
                    <option value="">Selecione a coluna...</option>
                    {dropdownOptions.map(option => (
                      <option key={option.key} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </MatchSelect>
                </MatchColumn>
              );
            })}
          </MatchGrid>
        </ScrollArea>
      </>
    );
  };

  const renderValidationStep = () => (
    <>
      <PageHeader>
        <PageTitle>Validar os dados</PageTitle>
      </PageHeader>

      <ValidationTools>
        <Dropdown
          compact
          selection
          placeholder="Ordenar por"
          value={sortField}
          options={[
            { key: 'rowNumber', value: 'rowNumber', text: 'Ordem original' },
            ...fields.map(field => ({
              key: field.key,
              value: field.key,
              text: field.label
            }))
          ]}
          onChange={(_, { value }) => setSortField(String(value || 'rowNumber'))}
        />
        <Dropdown
          compact
          selection
          value={sortDirection}
          options={[
            { key: 'ASC', value: 'ASC', text: 'Crescente' },
            { key: 'DESC', value: 'DESC', text: 'Decrescente' }
          ]}
          onChange={(_, { value }) =>
            setSortDirection((value as SortDirection) || 'ASC')
          }
        />
        <Button
          light
          type="button"
          disabled={!selectedRows.length || isSubmitting}
          onClick={handleDiscardSelected}
          text="Descartar linhas selecionadas"
        />
        <Checkbox
          toggle
          label="Mostrar apenas linhas com erros"
          checked={showOnlyErrors}
          onChange={(_, { checked }) => setShowOnlyErrors(Boolean(checked))}
        />
      </ValidationTools>

      {invalidRows.length > 0 && (
        <Message
          warning
          content={`${invalidRows.length} linha(s) possuem campos obrigatórios ausentes ou inválidos.`}
        />
      )}

      <TableWrapper>
        <StyledTable celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              {fields.map(field => (
                <Table.HeaderCell key={field.key}>{field.label}</Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedVisibleRows.slice(0, 120).map(row => (
              <Table.Row key={row.rowNumber} negative={Object.keys(row.errors).length > 0}>
                <Table.Cell collapsing>
                  <Checkbox
                    checked={selectedRows.includes(row.rowNumber)}
                    onChange={() => toggleSelectedRow(row.rowNumber)}
                  />
                </Table.Cell>
                {fields.map(field => (
                  <DataCell key={field.key} hasError={Boolean(row.errors[field.key])}>
                    {row.values[field.key] || '-'}
                  </DataCell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </StyledTable>
      </TableWrapper>

      {importResult && (
        <StatusMessage
          positive={!importResult.failed.length}
          warning={Boolean(importResult.failed.length)}
          style={{ marginTop: 16 }}
        >
          <Message.Header>Resultado da importação</Message.Header>
          <p>
            {importResult.success} de {importResult.total} imóveis foram importados.
          </p>
          {importResult.failed.length > 0 && (
            <p>
              Falhas:{' '}
              {importResult.failed
                .slice(0, 5)
                .map(fail => `linha ${fail.rowNumber}: ${fail.message}`)
                .join(' | ')}
            </p>
          )}
        </StatusMessage>
      )}
    </>
  );

  return (
    <PageContainer>
      <ImportShell>
        {renderSteps()}
        <ContentSegment>
          {currentStep === 1 && renderUploadStep()}
          {currentStep === 2 && renderHeaderStep()}
          {currentStep === 3 && renderMatchStep()}
          {currentStep === 4 && renderValidationStep()}
        </ContentSegment>
        <ActionsBar>
          <ActionsGroup>
            {currentStep > 1 && (
              <Button
                light
                type="button"
                disabled={isSubmitting}
                onClick={() => setCurrentStep((currentStep - 1) as Step)}
                text="Voltar"
              />
            )}
            {currentStep === 1 && (
              <Button
                light
                type="button"
                disabled={isSubmitting}
                onClick={() => history.push('/admin/properties')}
                text="Cancelar"
              />
            )}
            {currentStep === 2 && (
              <Button type="button" onClick={handleHeaderNext} text="Próximo" />
            )}
            {currentStep === 3 && (
              <Button type="button" onClick={handleMatchNext} text="Próximo" />
            )}
            {currentStep === 4 && (
              <Button
                type="button"
                loading={isSubmitting}
                disabled={isSubmitting || !validRows.length || isBackofficeTest}
                onClick={handleImport}
                text="Confirmar"
              />
            )}
          </ActionsGroup>
        </ActionsBar>
      </ImportShell>

      <Modal
        size="tiny"
        open={showRequiredModal}
        onClose={() => setShowRequiredModal(false)}
      >
        <Modal.Header>Nem todas as colunas correspondem</Modal.Header>
        <Modal.Content>
          <p>
            Existem colunas obrigatórias que não são correspondidas ou ignoradas.
          </p>
          <p>
            Colunas não correspondidas:{' '}
            <strong>{missingRequiredFields.map(field => field.label).join(', ')}</strong>
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            light
            type="button"
            text="Cancelar"
            onClick={() => setShowRequiredModal(false)}
          />
        </Modal.Actions>
      </Modal>
    </PageContainer>
  );
};

export default connect(mapState, null)(PropertiesImportPage);
